import React, { useEffect, useCallback } from "react";
import {
  X,
  Hammer,
  Pencil,
  Variable,
  GitBranch,
  Link2,
  ListChecks,
  AlertTriangle,
  CheckCircle2,
  Layers,
} from "lucide-react";
import {
  normalizePlanForDisplay,
  getPreviewBranches,
  getPreviewEdges,
  getPreviewVariableRows,
  getPreviewStats,
} from "./planPreviewUtils";

const NODE_TYPE_COLORS = {
  trigger: "bg-green-100 text-green-700",
  textMessage: "bg-sky-100 text-sky-700",
  condition: "bg-orange-100 text-orange-700",
  controller: "bg-amber-100 text-amber-700",
  delay: "bg-gray-100 text-gray-600",
  webhook: "bg-violet-100 text-violet-700",
  codeMap: "bg-blue-100 text-blue-700",
  jsonMap: "bg-blue-100 text-blue-700",
  interactiveButtons: "bg-indigo-100 text-indigo-700",
  interactiveList: "bg-indigo-100 text-indigo-700",
  interactiveUrl: "bg-indigo-100 text-indigo-700",
  interactiveCarouselUrl: "bg-pink-100 text-pink-700",
  interactiveCarouselButton: "bg-pink-100 text-pink-700",
  aiAskQuestion: "bg-purple-100 text-purple-700",
  captureText: "bg-emerald-100 text-emerald-800",
  aiIntentClassifier: "bg-purple-100 text-purple-700",
  aiEntityExtractor: "bg-purple-100 text-purple-700",
  aiResponseGenerator: "bg-purple-100 text-purple-700",
  aiKnowledgeBaseAnswer: "bg-purple-100 text-purple-700",
  knowledgeBase: "bg-teal-100 text-teal-700",
  assignAgent: "bg-rose-100 text-rose-700",
  transferToFlow: "bg-cyan-100 text-cyan-700",
  end: "bg-red-100 text-red-700",
};

const PlanPreviewModal = ({ open, onClose, plan, preview, onBuild, onModify }) => {
  const normalized = normalizePlanForDisplay(plan);
  const explicitEdges = getPreviewEdges(preview, plan);
  const branches = getPreviewBranches(preview, plan);
  const wiringSectionTitle = explicitEdges.length > 0 ? "Interconnections" : "Branches";
  const varRows = getPreviewVariableRows(preview, plan);
  const { totalPlannedNodes, totalPlannedConnections } = getPreviewStats(
    preview,
    plan,
    normalized
  );
  const cards = preview?.cards?.length ? preview.cards : null;
  const title = preview?.title || plan?.objective || "Flow plan preview";
  const subtitle = preview?.subtitle || "Review before build";
  const checklist = plan?.validationChecklist || plan?.validationNotes || [];
  const readiness = plan?.buildReadiness;
  const hints = preview?.uiHints || {};

  const showBuild = hints.showBuildButton !== false;
  const showModify = hints.showModifyPlanButton !== false;

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-preview-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[min(90vh,720px)] flex flex-col overflow-hidden border border-gray-200">
        <div className="shrink-0 px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/80 text-[10px] font-bold uppercase tracking-wider">
              <Layers size={12} />
              Plan preview
            </div>
            <h2 id="plan-preview-title" className="text-lg font-bold truncate">
              {title}
            </h2>
            <p className="text-xs text-white/90 mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/15 transition-colors shrink-0"
            aria-label="Close preview"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div className="flex flex-wrap gap-3">
            <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 text-center min-w-[100px]">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Nodes</p>
              <p className="text-xl font-bold text-slate-800">{totalPlannedNodes}</p>
            </div>
            <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 text-center min-w-[100px]">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Connections</p>
              <p className="text-xl font-bold text-slate-800">{totalPlannedConnections}</p>
            </div>
            {readiness && (
              <div
                className={`flex-1 min-w-[200px] px-3 py-2 rounded-xl border flex items-center gap-2 ${
                  readiness.ready
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
                }`}
              >
                {readiness.ready ? (
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle size={18} className="text-amber-600 shrink-0" />
                )}
                <div className="min-w-0 text-xs">
                  <p className="font-bold text-gray-800">
                    Build readiness:{" "}
                    <span className="capitalize">{readiness.riskLevel || "—"}</span> risk
                  </p>
                  {readiness.notes && (
                    <p className="text-gray-600 mt-0.5">{readiness.notes}</p>
                  )}
                  {readiness.missingUserInputs?.length > 0 && (
                    <ul className="mt-1 list-disc list-inside text-amber-800">
                      {readiness.missingUserInputs.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {Array.isArray(plan?.assumptions) && plan.assumptions.length > 0 && (
            <section>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                Assumptions
              </p>
              <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                {plan.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Layers size={12} />
              Flow timeline
            </p>
            {cards ? (
              <div className="space-y-2">
                {cards.map((c, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-indigo-100 bg-gradient-to-br from-slate-50 to-indigo-50/40 p-3"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                        {c.step ?? i + 1}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          NODE_TYPE_COLORS[c.nodeType] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.nodeType}
                      </span>
                      {c.chips?.map((chip, j) => (
                        <span
                          key={j}
                          className="text-[9px] px-1.5 py-0.5 bg-white/80 border border-indigo-100 rounded-full text-indigo-700 font-medium"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                    {c.headline && (
                      <p className="text-sm font-semibold text-gray-900 mt-2">{c.headline}</p>
                    )}
                    {c.userSummary && (
                      <p className="text-xs text-gray-800 mt-1 leading-relaxed font-medium">{c.userSummary}</p>
                    )}
                    {c.content && !c.userSummary && (
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{c.content}</p>
                    )}
                    {c.content && c.userSummary && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{c.content}</p>
                    )}
                    {c.nodeTypeRationale && (
                      <p className="text-[11px] text-indigo-700/90 mt-1.5 italic">{c.nodeTypeRationale}</p>
                    )}
                    {c.connectsTo?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.connectsTo.map((ct, ci) => (
                          <span
                            key={ci}
                            className="text-[10px] px-2 py-0.5 rounded-md bg-white border border-indigo-200 text-indigo-800 font-medium"
                          >
                            → Step {ct.step}
                            {ct.label ? `: ${ct.label}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : normalized.steps.length ? (
              <div className="space-y-2">
                {normalized.steps.map((s, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex gap-2"
                  >
                    <span className="w-7 h-7 rounded-full bg-gray-700 text-white text-xs font-bold flex items-center justify-center shrink-0">
                      {s.step ?? i + 1}
                    </span>
                    <div>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          NODE_TYPE_COLORS[s.nodeType] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {s.nodeType}
                      </span>
                      <p className="text-xs font-semibold text-gray-800 mt-0.5">{s.title || ""}</p>
                      <p className="text-xs text-gray-700 mt-1">{s.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">No step timeline in this plan.</p>
            )}
          </section>

          {branches.length > 0 && (
            <section>
              <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                {explicitEdges.length > 0 ? <Link2 size={12} /> : <GitBranch size={12} />}
                {wiringSectionTitle}
              </p>
              <div className="rounded-xl border border-violet-100 bg-violet-50/50 divide-y divide-violet-100">
                {branches.map((b, i) => (
                  <div
                    key={i}
                    className="px-3 py-2 flex flex-wrap items-center gap-2 text-xs"
                  >
                    <span className="font-mono font-bold text-violet-800">Step {b.fromStep}</span>
                    <span className="text-violet-400">→</span>
                    {b.kind && (
                      <span className="text-[9px] uppercase font-bold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">
                        {b.kind}
                      </span>
                    )}
                    <span
                      className={`font-semibold ${
                        b.fallback ? "text-amber-700" : "text-gray-800"
                      }`}
                    >
                      {b.label}
                    </span>
                    <span className="text-violet-400">→</span>
                    <span className="font-mono font-bold text-violet-800">Step {b.toStep}</span>
                    {b.fallback && (
                      <span className="text-[9px] uppercase font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                        fallback
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {varRows.length > 0 && (
            <section>
              <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Variable size={12} />
                Variables
              </p>
              <div className="rounded-xl border border-purple-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-purple-50 text-purple-900 font-bold">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Created</th>
                      <th className="text-left px-3 py-2">Used in</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-50">
                    {varRows.map((v, i) => (
                      <tr key={i} className="bg-white">
                        <td className="px-3 py-2 font-mono font-bold text-purple-800">
                          {`{{${v.name}}}`}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {v.createdAtStep != null
                            ? `Step ${v.createdAtStep}`
                            : v.producedBy || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {Array.isArray(v.usedAtSteps) && v.usedAtSteps.length
                            ? v.usedAtSteps.join(", ")
                            : v.description || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {checklist.length > 0 && (
            <section>
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                <ListChecks size={12} />
                Validation checklist
              </p>
              <ul className="text-xs space-y-1.5 bg-amber-50/80 border border-amber-100 rounded-xl px-3 py-3">
                {checklist.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-amber-900">
                    <span className="text-amber-500 font-bold mt-0.5">✓</span>
                    <span>{typeof item === "string" ? item : JSON.stringify(item)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-gray-100 bg-gray-50 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
          {showModify && (
            <button
              type="button"
              onClick={() => {
                onModify?.();
                onClose?.();
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-800 text-xs font-bold rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
            >
              <Pencil size={14} />
              Modify plan
            </button>
          )}
          {showBuild && (
            <button
              type="button"
              onClick={() => {
                onBuild?.();
                onClose?.();
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <Hammer size={14} />
              Build this flow
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanPreviewModal;
